import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import type { Annotation, Deck, Element, Page } from "@deckloom/core/schema";
import { walkElements } from "@deckloom/core/timeline";
import { PlayerContext, type PlayerContextValue } from "./context";
import { SlideView } from "./SlideView";
import { InkLayer, type InkTool } from "./InkLayer";
import { MediaLayer } from "./MediaLayer";
import { StepPlayer, type MediaCommand } from "./engine/StepPlayer";
import { MediaController, type MediaSyncState } from "./engine/MediaController";
import { loadDeckFonts } from "./fonts";
import { incomingOnTop, runTransition } from "./transitions";
import { usePreloadImages } from "./preload";

/** Where a click on a hyperlinked shape wants to go. */
export type NavigationTarget =
  | { kind: "page"; pageId: string }
  | { kind: "next" | "prev" | "first" | "last" | "end" };

export interface StageProps {
  deck: Deck;
  pageIndex: number;
  /** step to be at; -1 = before any step. Changing it by +1 plays, anything else seeks. */
  step: number;
  mediaUrl: (mediaId: string) => string;
  playUrl: (mediaId: string) => string;
  strokes?: Annotation[];
  /** the local user's unpublished drawings, shown faded on top (only to them) */
  drafts?: Annotation[];
  inkTool?: InkTool | null;
  authorId?: string;
  /** presenter gets native media controls and emits media events */
  presenter?: boolean;
  /** play slide transitions when the page changes (default true) */
  transitions?: boolean;
  onStroke?: (stroke: Annotation) => void;
  onStrokeRemove?: (id: string) => void;
  /** user interaction with media (presenter), to be broadcast */
  onMediaEvent?: (elementId: string, action: "play" | "pause" | "seek" | "stop", positionMs: number) => void;
  /** fired when an automatic step 0 started on page enter, so the controller can record position 0 */
  onAutoStep?: (step: number) => void;
  /** fired when a programmatic play() was blocked and fell back to muted playback */
  onMutedFallback?: () => void;
  /** a click on a shape started interactive sequence `seq` of this page (presenter broadcasts it) */
  onTrigger?: (seq: number) => void;
  /** a click on a hyperlinked shape (presenter only acts on it) */
  onNavigate?: (target: NavigationTarget) => void;
  onClick?: () => void;
  children?: ReactNode;
}

export interface StageHandle {
  /** apply a media sync/command coming from the room */
  applyMediaSync(sync: MediaSyncState, serverNow: number): void;
  applyMediaCommand(cmd: MediaCommand): void;
  /** play the next step of an interactive sequence (viewers, from the room) */
  applyTrigger(seq: number): void;
  /** positions of playing media (for the presenter heartbeat) */
  mediaSnapshot(): { elementId: string; position: number; playing: boolean }[];
  /** must be called from a user gesture on viewers */
  unlockMedia(): Promise<void>;
}

function elementById(page: Page, id: string): Element | undefined {
  for (const el of walkElements(page.elements)) if (el.id === id) return el;
  return undefined;
}

function navigationOf(el: Element): NavigationTarget | null {
  const h = el.hyperlink;
  if (!h) return null;
  if (h.kind === "slide") return { kind: "page", pageId: h.pageId };
  if (h.kind === "action") {
    const jump = /jump=(\w+)/.exec(h.action)?.[1];
    switch (jump) {
      case "nextslide": return { kind: "next" };
      case "previousslide": return { kind: "prev" };
      case "firstslide": return { kind: "first" };
      case "lastslide": return { kind: "last" };
      case "endshow": return { kind: "end" };
      default: return null;
    }
  }
  return null;
}

/** Fits the slide into its container, mounts the SVG, ink and media layers and drives the StepPlayer. */
export const Stage = forwardRef<StageHandle, StageProps>(function Stage(props, ref) {
  const { deck, pageIndex, step, mediaUrl, playUrl, strokes = [], drafts, inkTool = null, authorId = "local", presenter = false, transitions = true, onStroke, onStrokeRemove, onMediaEvent, onAutoStep, onMutedFallback, onTrigger, onNavigate, onClick, children } = props;
  const page: Page | undefined = deck.pages[pageIndex];
  const svgRef = useRef<SVGSVGElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const currentLayerRef = useRef<HTMLDivElement>(null);
  const outgoingLayerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<StepPlayer | null>(null);
  const lastStep = useRef<number>(-2);
  const [fontsReady, setFontsReady] = useState(false);
  const ctx = useMemo<PlayerContextValue>(() => ({ deck, mediaUrl, playUrl }), [deck, mediaUrl, playUrl]);
  const onMediaEventRef = useRef(onMediaEvent);
  onMediaEventRef.current = onMediaEvent;

  // Page changes: the previous page stays mounted in an overlay until the new one can be drawn whole (its pictures
  // decoded), and through the transition when the page has one.
  const prevPageRef = useRef<Page | undefined>(undefined);
  const [outgoing, setOutgoing] = useState<{ page: Page; key: number; onTop: boolean; animated: boolean } | null>(null);
  const transitionAnims = useRef<Animation[]>([]);

  // One media controller per page.
  const controller = useMemo(
    () => (page ? new MediaController(deck, page, { onLocalEvent: (id, action, pos) => presenter && onMediaEventRef.current?.(id, action, pos) }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deck, page?.id, presenter],
  );
  useEffect(() => () => controller?.destroy(), [controller]);

  // the next slides' pictures fetched before they are asked for, and a page's pictures awaited before it shows
  const imagesReady = usePreloadImages(deck, pageIndex, mediaUrl);

  useEffect(() => {
    let alive = true;
    loadDeckFonts(deck).then(() => alive && setFontsReady(true));
    return () => { alive = false; };
  }, [deck]);

  // Detect page changes: keep the page left over the new one (a cut), or start the transition into the new one.
  useLayoutEffect(() => {
    const prev = prevPageRef.current;
    prevPageRef.current = page;
    if (!page || !prev || prev.id === page.id) return;
    const t = page.transition;
    const animated = transitions && Boolean(t) && t!.kind !== "none" && t!.kind !== "cut";
    for (const a of transitionAnims.current) a.cancel();
    setOutgoing({ page: prev, key: Date.now(), onTop: animated ? !incomingOnTop(t!.kind) : true, animated });
  }, [page, transitions]);

  // The new page is hidden until its pictures are decoded (so no frame shows it half drawn, which read as a
  // flicker); then it cuts in, or its transition runs with the page left in the other layer.
  useLayoutEffect(() => {
    if (!outgoing || !page) return;
    const out = outgoingLayerRef.current, cur = currentLayerRef.current;
    if (!out || !cur) { setOutgoing(null); return; }
    cur.style.visibility = "hidden";
    let alive = true;
    let anims: Animation[] = [];
    void imagesReady(page).then(() => {
      if (!alive) return;
      cur.style.visibility = "";
      if (!outgoing.animated || !page.transition) { setOutgoing(null); return; }
      anims = runTransition(page.transition, out, cur);
      transitionAnims.current = anims;
      if (anims.length === 0) { setOutgoing(null); return; }
      return Promise.all(anims.map((a) => a.finished.catch(() => undefined))).then(() => {
        if (!alive) return;
        // the page left goes out of sight before its animations are cancelled: cancelling drops their end state (a
        // fade's opacity back to 1), and the unmount that removes the layer may paint a frame later
        out.style.visibility = "hidden";
        for (const a of anims) a.cancel();
        cur.style.transform = ""; cur.style.clipPath = ""; cur.style.opacity = "";
        setOutgoing(null);
      });
    });
    return () => { alive = false; cur.style.visibility = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoing?.key]);

  // (Re)create the step player whenever the page changes.
  useLayoutEffect(() => {
    if (!page || !svgRef.current || !rootRef.current || !controller) return;
    const roots: ParentNode[] = [svgRef.current];
    const mediaLayer = rootRef.current.querySelector(".media-layer");
    if (mediaLayer) roots.push(mediaLayer);
    const player = new StepPlayer(roots, deck, page, { onMedia: (cmd) => controller.apply(cmd) });
    playerRef.current = player;
    const entered = player.enter();
    lastStep.current = entered;
    if (entered === 0 && step < 0) onAutoStep?.(0);
    if (step >= 0 && step !== entered) {
      player.seek(step);
      lastStep.current = step;
    }
    return () => { player.destroy(); playerRef.current = null; lastStep.current = -2; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id, deck, fontsReady, controller]);

  // Step changes on the same page.
  useLayoutEffect(() => {
    const player = playerRef.current;
    if (!player || lastStep.current === -2 || step === lastStep.current) return;
    if (step === lastStep.current + 1) player.play(step); else player.seek(step);
    lastStep.current = step;
  }, [step]);

  useImperativeHandle(ref, () => ({
    applyMediaSync: (sync, serverNow) => controller?.applySync(sync, serverNow),
    applyMediaCommand: (cmd) => controller?.apply(cmd),
    applyTrigger: (seq) => { playerRef.current?.trigger(seq); },
    mediaSnapshot: () => controller?.snapshot() ?? [],
    unlockMedia: async () => { await controller?.unlock(); },
  }), [controller]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !onMutedFallback) return;
    const h = () => onMutedFallback();
    root.addEventListener("deckloom:muted-fallback", h);
    return () => root.removeEventListener("deckloom:muted-fallback", h);
  }, [onMutedFallback]);

  // Clicks on shapes: interactive triggers and hyperlinks come before "advance".
  const handleClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const player = playerRef.current;
    if (page && player && !inkTool) {
      const ids: string[] = [];
      let node = (e.target as HTMLElement | null)?.closest?.("[data-el]") as HTMLElement | null;
      while (node) {
        const id = node.dataset.el;
        if (id && !id.includes("#")) ids.push(id);
        node = node.parentElement?.closest("[data-el]") as HTMLElement | null;
      }
      const seq = player.triggerFor(ids);
      if (seq >= 0) {
        e.stopPropagation();
        player.trigger(seq);
        onTrigger?.(seq);
        return;
      }
      for (const id of ids) {
        const el = elementById(page, id);
        if (!el) continue;
        const nav = navigationOf(el);
        if (nav) { e.stopPropagation(); onNavigate?.(nav); return; }
        if (el.hyperlink?.kind === "url") { e.stopPropagation(); window.open(el.hyperlink.href, "_blank", "noopener"); return; }
      }
    }
    onClick?.();
  }, [page, inkTool, onTrigger, onNavigate, onClick]);

  if (!page || !controller) return null;
  const layer = (onTop: boolean): CSSProperties => ({ position: "absolute", inset: 0, zIndex: onTop ? 2 : 1, willChange: "transform, opacity, clip-path" });
  return (
    <PlayerContext.Provider value={ctx}>
      <div
        ref={rootRef}
        className="stage"
        data-fonts-ready={fontsReady ? "1" : "0"}
        style={{ position: "relative", width: "100%", height: "100%", aspectRatio: `${deck.size.w} / ${deck.size.h}`, overflow: "hidden", background: "#000" }}
        onClick={handleClick}
      >
        <div ref={currentLayerRef} className="slide-layer" style={layer(!outgoing || !outgoing.onTop)}>
          <SlideView ref={svgRef} page={page} />
        </div>
        {outgoing && (
          <div ref={outgoingLayerRef} key={outgoing.key} className="slide-layer outgoing" style={{ ...layer(outgoing.onTop), pointerEvents: "none" }}>
            <div style={{ position: "absolute", inset: 0 }}>
              <SlideView page={outgoing.page} allVisible />
            </div>
          </div>
        )}
        <div className="stage-overlays" style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" }}>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            <MediaLayer
              page={page}
              controller={controller}
              controls={presenter}
              onToggle={(id) => { if (presenter) controller.apply({ elementId: id, action: "togglePause" }); }}
            />
          </div>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            <InkLayer strokes={strokes} drafts={drafts} tool={inkTool} authorId={authorId} onStroke={onStroke} onRemove={onStrokeRemove} />
          </div>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>{children}</div>
        </div>
      </div>
    </PlayerContext.Provider>
  );
});
